// Pure display/derivation helpers for per-admin station & contractor-type
// scope (see migration 0060: NULL/empty = unrestricted) and, since spec
// 2026-09-03, the role predicates that decide what the Users page may show.
// Everything here is a function of plain strings so it can be tested without a
// DOM -- apps/admin has no jsdom -- and Users.tsx only chooses a control.

export type AdminRole = 'viewer' | 'reviewer' | 'super_admin';

// The ONE place that decides "may change something" (spec D4). An allow-list,
// so an unknown or absent role fails closed onto the viewer floor -- including
// the string 'admin', which stopped being a role at migration 0090 and must
// not be honoured by a token minted before it.
//
// This replaced isAdminOrAbove, which conflated two authorities that now sit
// on different tiers: configuring the portal (isSuperAdmin below) and working
// a vendor (this). Mirrors requireWriteRole in
// apps/api/src/middleware/adminAuth.ts -- the server is the enforcement; this
// only decides whether to draw a control the API would refuse.
export function canWrite(role: string | undefined): boolean {
  return role === 'reviewer' || role === 'super_admin';
}

export function isSuperAdmin(role: string | undefined): boolean {
  return role === 'super_admin';
}

// Client mirror of apps/api/src/lib/adminScope.ts effectiveAssignment (D15).
// The API is the enforcement point; this exists so the sidebar caption, the
// station list, the scope cells and the invite pickers agree with what the
// server applies. Scope is orthogonal to tier (D14) -- no role branch.
export function effectiveAssignment(assigned: string[] | null | undefined): string[] | null {
  return assigned && assigned.length > 0 ? assigned : null;
}

// Falls back to 'Viewer' on an unknown role: this drives display only, and
// naming an unrecognised role after the floor is the honest guess -- including
// the stale 'admin' a token minted before 0090 still carries.
export function roleLabel(role: string): string {
  if (role === 'super_admin') return 'Super admin';
  if (role === 'reviewer') return 'Reviewer';
  return 'Viewer';
}

// Roles this viewer may assign, at invite time and in the role dropdown.
// Nobody below super admin reaches the roster any more, so every other tier
// gets an EMPTY list rather than a shorter one: a widened route must not find
// a stale picker willing to mint tiers.
export function roleOptionsFor(viewerRole: string | undefined): AdminRole[] {
  return isSuperAdmin(viewerRole) ? ['viewer', 'reviewer', 'super_admin'] : [];
}

// The four row capabilities. All four are super-admin-only since the viewer
// tier landed: the roster is Settings content, and Settings is the top tier's
// authority now. The server agrees -- every /api/users route sits behind
// requireSuperAdminRole.
export function canEditName(viewerRole: string | undefined): boolean {
  return isSuperAdmin(viewerRole);
}

/**
 * May this viewer change this target's role?
 *
 * The TARGET's role stopped mattering when the roster became
 * super-admin-only: the only actor who reaches this control is already the top
 * tier, so there is no tier above them left to protect. `targetRole` is kept
 * in the signature because Users.tsx passes it per row and a future tier would
 * need it again.
 *
 * Never your own row. A super admin demoting themselves through the UI could
 * strand the portal with no super admin at all, and the runbook's recovery
 * section is the only way back.
 */
export function canEditRole(
  viewerRole: string | undefined,
  _targetRole: string,
  isSelf: boolean,
): boolean {
  if (isSelf) return false;
  return isSuperAdmin(viewerRole);
}

// D17: scope editing follows the rest of the roster to the top tier. What
// still bounds a super admin is the server's delta merge (D19), which
// preserves the portion of the target's scope outside their reach and refuses
// to empty it -- not a client predicate.
export function canEditScope(viewerRole: string | undefined): boolean {
  return isSuperAdmin(viewerRole);
}

export function canDeleteUser(viewerRole: string | undefined, isSelf: boolean): boolean {
  return isSuperAdmin(viewerRole) && !isSelf;
}

// Review finding 5. The old copy said "leave empty for all stations", which is
// true only for an unrestricted inviter: clampToInviter substitutes a scoped
// inviter's OWN list, and they cannot then correct it. Say what will happen.
export function scopeHint(mine: string[] | null, noun: string): string {
  return mine === null
    ? `Leave empty for all ${noun}.`
    : `Leave empty for your own ${noun}. You cannot grant access wider than your own.`;
}

export function listSummary(values: string[] | null, noun: string): string {
  if (!values || values.length === 0) return `All ${noun}`;
  if (values.length === 1) return values[0]!;
  return `${values.length} ${noun}`;
}

export function accessSummary(stations: string[] | null, types: string[] | null): string {
  return `${listSummary(stations, 'stations')} · ${listSummary(types, 'types')}`;
}

// The stations a user can pick as "active": their effective assignment, or
// every configured station when unrestricted. No live caller since Task 17
// removed the switcher (AdminShell.tsx); kept so a re-introduced switcher
// inherits the rule instead of rediscovering it.
export function visibleStations(assigned: string[] | null, configured: string[]): string[] {
  return effectiveAssignment(assigned) ?? configured;
}

// Sidebar caption under the user's email: the active station if narrowed,
// otherwise the effective assignment summary ("All stations", "2 stations", …).
export function scopeSummaryLabel(assigned: string[] | null, activeStation: string | null): string {
  if (activeStation) return activeStation;
  return listSummary(effectiveAssignment(assigned), 'stations');
}

// Assignment-editor options: configured list plus any already-assigned values
// no longer configured, so a revoked option remains visible and removable.
export function mergedOptions(configured: string[], assigned: string[] | null): string[] {
  const extra = (assigned ?? []).filter((v) => !configured.includes(v));
  return [...configured, ...extra];
}

// I2: the values on a target's stored scope that lie outside the granter's
// own reach. mergeScopeEdit (api/lib/adminScope.ts) preserves exactly these
// untouched no matter what the granter submits -- D19's delta merge, not a
// replacement -- so an unticked box that silently reappears on refetch is
// correct, not a bug. Naming them is what keeps it from reading as one.
// Unrestricted granter (`mine === null`) has nothing outside their reach.
export function outOfReach(mine: string[] | null, target: string[] | null): string[] {
  if (mine === null) return [];
  return (target ?? []).filter((v) => !mine.includes(v));
}

/**
 * Narrow a configured option list to an assignment scope.
 *
 * `null`/`undefined`/`[]` scope = unrestricted, so the configured list passes
 * through unchanged — matching the empty-means-unrestricted rule the server
 * enforces in adminScope.ts's normalize(). Otherwise the result is the
 * intersection, in `configured` order (the Settings-curated order the whole UI
 * shows these in).
 *
 * `alwaysInclude` values survive the narrowing and are appended after, deduped.
 * That is the fold-in rule mergedOptions established for revoked options,
 * extended to scope: a value already saved on the record being edited stays
 * visible and removable rather than vanishing from the picker and being
 * silently dropped on the next save.
 */
export function scopedOptions(
  configured: string[],
  scope: string[] | null | undefined,
  alwaysInclude: string[] = [],
): string[] {
  const narrowed =
    scope && scope.length > 0 ? configured.filter((v) => scope.includes(v)) : [...configured];
  const extra = alwaysInclude.filter((v) => !narrowed.includes(v));
  return [...narrowed, ...extra];
}

/**
 * Role filter chips for the Users page: only the roles this viewer's roster
 * actually contains, plus whatever is already ticked.
 *
 * `canSeeUser` (api adminScope.ts) hides an unrestricted target from a
 * station-scoped viewer, but scope is orthogonal to tier (spec D14): a
 * station-scoped super admin is routinely visible to, and appears in the
 * rows of, another super admin at the same station. So a scoped viewer can and does
 * receive super-admin rows -- only an UNRESTRICTED target (the global owner)
 * is ever hidden, by rule 3, regardless of tier.
 *
 * Derived from the ROWS rather than from the viewer's own role, and that is
 * the whole point: whether a "Super admin" chip is useful depends on what the
 * roster actually contains for THIS viewer, not on the viewer's own tier --
 * gating it on isSuperAdmin would hide a control that works for a plain admin
 * whose roster does contain one.
 *
 * `selected` folds in so a chip cannot vanish the moment its last row does and
 * strand the table behind a filter with no checkbox left to untick.
 *
 * Lives here, not inline in Users.tsx, because apps/admin has no DOM harness:
 * the multiselect renders its options only while open, so this is untestable
 * through renderToStaticMarkup and a pure function is the only place it can be
 * pinned.
 */
export function roleFilterOptions(
  canonical: string[],
  rows: { role: string }[],
  selected: string[],
): string[] {
  return scopedOptions(
    canonical,
    rows.map((r) => r.role),
    selected,
  );
}

// ---------------------------------------------------------------------------
// Sidebar nav: role filtering + group headers. Pure (moved out of
// AdminShell.tsx) so both are unit-testable without rendering the shell.

export type NavGroup = 'overview' | 'operate' | 'configure' | 'monitor' | 'admin';

export const GROUP_LABEL: Record<NavGroup, string> = {
  overview: 'Overview',
  operate: 'Operate',
  configure: 'Configure',
  monitor: 'Monitor',
  admin: 'Admin',
};

export type GroupedRow<T> =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; row: T };

export function groupNav<T extends { to: string; group: NavGroup }>(rows: T[]): GroupedRow<T>[] {
  const out: GroupedRow<T>[] = [];
  let last: NavGroup | null = null;
  for (const r of rows) {
    if (r.group !== last) {
      out.push({ kind: 'header', key: `h-${r.group}`, label: GROUP_LABEL[r.group] });
      last = r.group;
    }
    out.push({ kind: 'item', key: r.to, row: r });
  }
  return out;
}

/**
 * Nav rows a user of `role` may see: only a super admin gets the adminOnly rows
 * (Questionnaires, Documents, Settings) since the viewer tier landed —
 * configuring the portal is the top tier's authority now. Callers filter
 * BEFORE groupNav — groupNav emits a header on each group transition, so
 * removing the rows removes their now-empty group captions for free;
 * filtering afterwards would leave orphaned headers.
 */
export function navForRole<T extends { adminOnly?: boolean }>(rows: T[], role: string): T[] {
  return isSuperAdmin(role) ? rows : rows.filter((r) => !r.adminOnly);
}

/**
 * Route-guard decision for the admin-only pages (/questionnaires, /questions,
 * /documents, /settings): anything but a super admin is redirected to the
 * Dashboard, so a reviewer following a stale bookmark lands somewhere useful
 * instead of a 403 dead end. Fails closed on an unknown role, matching
 * navForRole above and the server's requireSuperAdminRole — which remains the
 * real enforcement. An absent
 * role cannot reach here in practice: every gated route is a child of
 * AdminShell, which renders a spinner while auth loads and redirects to /login
 * when there is no user.
 */
export function isAdminOnlyRouteAllowed(role: string | undefined): boolean {
  return isSuperAdmin(role);
}
